import FilterListIcon from '@mui/icons-material/FilterList';
import { Box } from '@mui/material';
import cn from 'classnames';
import { addYears } from 'date-fns';
import React, { forwardRef, Ref, useCallback, useImperativeHandle, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { DateRangePicker } from '../../../components';
import {
  ClearFiltersButton,
  DepartmentFilter,
  FilterSelect,
  InfiniteScrollSelect,
  SegmentedControl,
  ViewSwitcher,
} from '../../../components/_mui';
import { filtersContainerStyles, filtersRowStyles } from '../../../components/_mui/styles/TableFilters.styles';
import { CUSTOM_FILTER_OPTION } from '../../../constants/campaigns';
import { DISTRIBUTOR, ORG_ADMIN, SUPER_ADMIN, USER } from '../../../constants/users';
import useWindowSize from '../../../hooks/useWindowSize';
import { changeReportsActiveView } from '../../../store/actions/shell';
import { selectAdminType, selectIsAdminTypeUser } from '../../../store/selectors/auth';
import { selectCurrentOrganizationDepartmentList } from '../../../store/selectors/organizations';
import { selectReportsActiveView } from '../../../store/selectors/shell';
import { ReportStatusEnum } from '../../../types/reports';
import { FilterTypesEnum, IDatePickerRef, ViewTypesEnum } from '../../../types/shell';
import { IFilterItem, IFiltersRef } from '../../../types/tables';
import { hasPermission } from '../../../utils/users';

import styles from '../ReportsContainer.module.scss';

interface IProps {
  activeTab: number;
  setActiveTab: (tab: number) => void;
  renderSearchField: React.JSX.Element | null;
  renderDownloadButton: React.JSX.Element | null;
  filterConfig: IFilterItem[];
  dateRange: {
    from: string | null;
    to: string | null;
  };
  handleSelectDateRange: (value: { from?: string | null; to?: string | null }) => void;
  onSearchReset: () => void;
  query?: string;
  datePickerRef: Ref<IDatePickerRef>;
  selectedDepartments: string[];
  handleSelectDepartments: (departments: string[]) => void;
  selectedItemName?: string;
  setSelectedItemName?: (name: string | undefined) => void;
  selectedCampaignName?: string;
  setSelectedCampaignName?: (name: string | undefined) => void;
}

const headerStyles = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
  flexDirection: { xs: 'column', sm: 'row' },
  gap: { xs: 2, sm: 0 },
};

const searchSwitcherStyles = {
  display: 'flex',
  flexDirection: { xs: 'column', sm: 'row' },
  alignItems: 'center',
  gap: { xs: 1, sm: 2.5 },
  width: { xs: '100%', sm: 'auto' },
  mb: { xs: 2, sm: 0 },
};

const Filters = forwardRef<IFiltersRef, IProps>(
  (
    {
      activeTab,
      setActiveTab,
      renderDownloadButton,
      renderSearchField,
      filterConfig,
      dateRange,
      handleSelectDateRange,
      onSearchReset,
      query,
      datePickerRef,
      selectedDepartments,
      handleSelectDepartments,
      selectedItemName,
      setSelectedItemName,
      selectedCampaignName,
      setSelectedCampaignName,
    }: IProps,
    filtersRef,
  ) => {
    const dispatch = useDispatch();
    const adminType = useSelector(selectAdminType);
    const isAdminTypeUser = useSelector(selectIsAdminTypeUser);
    const activeView = useSelector(selectReportsActiveView);
    const allDepartments = useSelector(selectCurrentOrganizationDepartmentList) ?? [];
    const { mobile, smallTablet } = useWindowSize();

    const isDeptFilterShown = useMemo(
      () => isAdminTypeUser && hasPermission([ORG_ADMIN, SUPER_ADMIN, DISTRIBUTOR], adminType),
      [isAdminTypeUser, adminType],
    );

    const handleSetActiveView = useCallback(
      (view: ViewTypesEnum) => {
        dispatch(changeReportsActiveView(view));
      },
      [dispatch],
    );

    const isAnyFilterActive = useMemo(() => {
      return (
        filterConfig.some((filter) => filter.value && filter?.value?.length > 0) ||
        dateRange?.from ||
        dateRange?.to ||
        (query && query?.length > 0) ||
        selectedDepartments.length > 0
      );
    }, [dateRange?.from, dateRange?.to, filterConfig, query, selectedDepartments]);

    const handleClearFilter = useCallback((onChange: (value: string | string[]) => void) => {
      onChange('');
    }, []);

    const handleClearAllFilters = useCallback(() => {
      filterConfig.forEach((filter) => filter.onChange(''));
      handleSelectDepartments([]);

      handleSelectDateRange({
        from: null,
        to: null,
      });

      if (onSearchReset) {
        onSearchReset();
      }
    }, [filterConfig, handleSelectDepartments, handleSelectDateRange, onSearchReset]);

    useImperativeHandle(
      filtersRef,
      () => ({
        resetFilters: handleClearAllFilters,
      }),
      [handleClearAllFilters],
    );

    return (
      <Box sx={{ width: '100%' }}>
        <Box sx={headerStyles}>
          <Box sx={searchSwitcherStyles}>
            {renderSearchField}
            <SegmentedControl
              options={[
                { label: 'Regular', value: 'regular', dataAction: 'reports_regular_tab' },
                { label: 'Scheduled', value: 'scheduled', dataAction: 'reports_scheduled_tab' },
              ]}
              value={activeTab === 0 ? 'regular' : 'scheduled'}
              onChange={(value) => setActiveTab(value === 'regular' ? 0 : 1)}
              dimensions={{
                containerWidth: 161,
                containerHeight: 40,
                buttonWidth: 75,
                buttonHeight: 32,
              }}
            />
          </Box>
          {!mobile && renderDownloadButton}
        </Box>
        <Box sx={filtersRowStyles}>
          <Box sx={filtersContainerStyles}>
            <FilterListIcon color="disabled" />
            {filterConfig.map(
              ({
                tabId,
                label,
                value,
                onChange,
                options,
                isMultiple = false,
                isVisible = true,
                isIconVisible = true,
                limit,
                onLimitReached,
                useInfiniteScroll = false,
                autocompleteConfig,
                dataHook,
                dataAction,
              }) => {
                const isItemFilter = tabId === FilterTypesEnum.Item;
                const isCampaignFilter = tabId === FilterTypesEnum.Campaign;
                const handleName = isItemFilter ? setSelectedItemName : setSelectedCampaignName;
                const name = isItemFilter ? selectedItemName : selectedCampaignName;
                const isInfiniteScrollSelector = useInfiniteScroll && autocompleteConfig && dataHook;
                const isStatusFilter = tabId === FilterTypesEnum.Status;

                return (
                  <React.Fragment key={tabId}>
                    {isVisible ? (
                      isInfiniteScrollSelector ? (
                        <InfiniteScrollSelect
                          key={tabId}
                          label={label}
                          value={value as string | undefined}
                          onChange={onChange}
                          onClear={handleClearFilter}
                          onNameChange={handleName}
                          displayName={name}
                          isIconVisible={isIconVisible}
                          config={autocompleteConfig}
                          useDataHook={dataHook}
                          dataAction={dataAction}
                          staticOptions={isCampaignFilter ? CUSTOM_FILTER_OPTION : undefined}
                        />
                      ) : (
                        <FilterSelect
                          limit={limit}
                          onLimitReached={onLimitReached}
                          isMultiple={isMultiple}
                          key={tabId}
                          label={label}
                          value={value as string | ReportStatusEnum}
                          onChange={onChange}
                          options={options}
                          onClear={handleClearFilter}
                          dataAction={dataAction}
                        />
                      )
                    ) : null}
                    {isStatusFilter && isDeptFilterShown && (
                      <DepartmentFilter
                        value={selectedDepartments}
                        onChange={handleSelectDepartments}
                        departments={allDepartments}
                      />
                    )}
                  </React.Fragment>
                );
              },
            )}
            <DateRangePicker
              pickerClassName={cn(styles.datePicker, { [styles.right]: hasPermission(USER, adminType) })}
              filtersRef={filtersRef}
              value={dateRange}
              onSelect={handleSelectDateRange}
              wide={!smallTablet}
              maxDate={addYears(new Date(), 10)}
              labelClassName={styles.dateFilterLabel}
              muiArrow
              ref={datePickerRef}
            />
            {isAnyFilterActive && <ClearFiltersButton onClick={handleClearAllFilters} />}
          </Box>
          <ViewSwitcher activeView={activeView} setActiveView={handleSetActiveView} />
        </Box>
      </Box>
    );
  },
);

export default Filters;
